"use client"

import React from 'react';
import {IDetectedBarcode, Scanner} from "@yudiel/react-qr-scanner";
import styles from "./ScanSegment.module.css";
import {Box} from "@mui/material";
import {useLocale, useTranslations} from "use-intl";
import {useAppStore} from "@/store/AppStore";
import {QRJsonRequest} from "@/utils/api/QRJson";
import {pathStatusRequest} from "@/utils/api/PathStatus";
import {pathRequest} from "@/utils/api/Path";
import {segmentRequest} from "@/utils/api/Segment";
import {Scan} from "@/types/Scan";
import {Segment} from "@/types/Segments";
import {useRouter} from "@/i18n/routing";
import useWalkStatus from "@/utils/hooks/useWalkStatus";

const ScanSegment = (props: { handleNext: () => void }) => {
  const locale = useLocale()
  const t = useTranslations("Walk")
  const walkStatus = useWalkStatus()
  const router = useRouter()
  const user = useAppStore((state) => state.user);
  const setRefresh = useAppStore((state) => state.setRefresh);
  const setQrCode = useAppStore((state) => state.setQrCode);
  const setScanningDirection = useAppStore((state) => state.setScanningDirection);
  const setScanningPath = useAppStore((state) => state.setScanningPath);
  const setScanningSegments = useAppStore((state) => state.setScanningSegments);
  const setScanningTransport = useAppStore((state) => state.setScanningTransport);
  const cleanResultsAndErrors = useAppStore((state) => state.cleanResultsAndErrors);

  const handleScan = async (result: IDetectedBarcode[]) => {
    const url = result.at(0)?.rawValue
    if (!url) return false
    const actionData = await QRJsonRequest(url + '?fromapp=true', String(user?.access_token), locale)
    setQrCode(actionData)
    cleanResultsAndErrors()
    if (actionData && user?.access_token) {
      const pid = walkStatus?.paths?.filter((itm) => {
        return itm.current
      }).at(0)?.path_id.toString() || actionData[0].path_id.toString()

      if (pid) {
        const fetchedData = await fetchData(user.access_token, pid)
        if (fetchedData) {
          await fetchPathData(pid)
        } else {
          router.refresh();
        }
      }

      const currentPathData = pid ? actionData.filter((itm: Scan) => {
        return String(itm.path_id) === String(pid)
      }) : actionData

      const dataPromises = currentPathData?.map(async (item: Scan) => {
        return await segmentRequest(item.segment_id, locale)
      })

      const datas = await Promise.all(dataPromises)
      const filtered = datas.filter((itm: false | Segment) => {
        return itm !== false
      })
      if (datas) {
        setScanningSegments(filtered as Array<Segment>)
      }
    }
    props.handleNext()
  }

  /* GET PATH STATUS */
  const fetchData = async (token: string, pathId: string) => {
    const fetched = await pathStatusRequest(token, String(pathId))
    if (false === fetched) {
      setRefresh(true)
      return true
    }
    if (fetched && fetched?.instances && fetched?.actions.length > 0) {
      const instance = fetched?.instances?.at(0)
      if (instance) {
        setScanningDirection(instance.direction)
        setScanningTransport(instance.transport)
      }
    } else {
      setScanningDirection(undefined)
      setScanningTransport(undefined)
    }
    return true
  }

  /* GET PATH INFO */
  const fetchPathData = async (pathId: string) => {
    const fetched = await pathRequest(String(pathId))
    if (fetched) {
      setScanningPath(fetched)
    }
    return
  }

  return (
    <Box sx={{marginTop: '2rem'}}>
      <h3 className={styles.title}>{t('scan')}</h3>
      <div className={styles.scanner}>
        <Scanner onScan={(result) => handleScan(result)}/>
      </div>
    </Box>
  );
};

export default ScanSegment;
